/**
 * Presentation helpers for the independent quotation review (FR-3.26a, HS2-XG16AM),
 * shared by the Summary overview, the line items, and the PDF/XLSX exports so every
 * surface names a finding and cites its source the same way. Types only from the
 * contract, so no zod value reaches the SPA bundle.
 */
import type { QuotationReviewT, ReviewCitationT, ReviewFindingT } from './contract/analysis.js';

type Severity = ReviewFindingT['severity'];

/** Display order of the severity groups. */
export const REVIEW_SEVERITY_ORDER: readonly Severity[] = ['high', 'medium', 'low'];

/** The heading each severity group shows. */
export const REVIEW_SEVERITY_LABELS: Record<Severity, string> = {
  high: 'Needs Attention',
  medium: 'Check Before Comparing',
  low: 'For Information',
};

/** A short name for each finding kind. */
export const REVIEW_KIND_LABELS: Record<ReviewFindingT['kind'], string> = {
  'line-arithmetic': 'Arithmetic',
  'grand-total': 'Grand total',
  'not-quoted': 'Not quoted',
  'not-on-demand': 'Not on demand',
  'partial-quantity': 'Partial quantity',
  'spec-mismatch': 'Spec mismatch',
  'mixed-currency': 'Mixed currency',
  'mixed-basis': 'Mixed basis',
  'price-outlier': 'Price outlier',
  terms: 'Terms',
  expired: 'Expired',
};

/** A source location when known, falling back to the review's quote-row ref. */
export function reviewCitationLabel(citation: ReviewCitationT): string {
  const parts = [citation.vendorName, citation.documentName ?? 'entered by hand'];
  if (citation.sourcePage != null) parts.push(`page ${String(citation.sourcePage)}`);
  if (citation.sourceSheet != null) parts.push(`sheet ${citation.sourceSheet}`);
  if (citation.sourceRow != null) parts.push(`row ${String(citation.sourceRow)}`);
  else if (citation.sourcePage == null && citation.row !== null) parts.push(`row ${String(citation.row)}`);
  return parts.join(', ');
}

/** A finding's source line: kind, line, citations, and whether the AI judged it. */
export function reviewFindingMeta(finding: ReviewFindingT): string {
  const parts = [REVIEW_KIND_LABELS[finding.kind]];
  if (finding.lineNumber !== null) parts.push(`Line ${String(finding.lineNumber)}`);
  for (const citation of finding.citations) parts.push(reviewCitationLabel(citation));
  if (finding.source === 'ai') parts.push('AI review');
  return parts.join(' · ');
}

/** The review's findings about one demand line, most severe first (stored order). */
export function lineReviewFindings(review: QuotationReviewT | undefined, lineItemId: string): ReviewFindingT[] {
  return (review?.findings ?? []).filter((finding) => finding.lineItemId === lineItemId);
}

/** The findings grouped by severity, in display order, omitting empty groups. */
export function reviewFindingGroups(
  review: QuotationReviewT,
): { severity: Severity; label: string; findings: ReviewFindingT[] }[] {
  return REVIEW_SEVERITY_ORDER.map((severity) => ({
    severity,
    label: REVIEW_SEVERITY_LABELS[severity],
    findings: review.findings.filter((finding) => finding.severity === severity),
  })).filter((group) => group.findings.length > 0);
}
